import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  level?: 'page' | 'component' | 'app';
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  level = 'page'
}) => {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleReload = () => {
    window.location.reload();
  };

  // App-level error: Full-screen branded error page
  if (level === 'app') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#5D1828] via-[#7A2235] to-[#8B2E3F] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle size={32} className="text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Algo salió mal
          </h1>
          <p className="text-gray-600 mb-6">
            La aplicación encontró un error inesperado. Por favor, recarga la página.
          </p>
          {import.meta.env.DEV && (
            <details className="text-left mb-6 p-4 bg-gray-100 rounded-lg">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                Detalles del error (desarrollo)
              </summary>
              <pre className="mt-2 text-xs text-red-600 overflow-auto max-h-40 whitespace-pre-wrap">
                {error.message}
                {'\n\n'}
                {error.stack}
              </pre>
            </details>
          )}
          <button
            onClick={handleReload}
            className="flex items-center gap-2 px-4 py-2 bg-[#5D1828] text-white rounded-lg hover:bg-[#7A2235] transition-colors mx-auto"
          >
            <RefreshCw size={16} />
            Recargar página
          </button>
        </div>
      </div>
    );
  }

  // Page-level error: Card with navigation options
  if (level === 'page') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={28} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Error al cargar la sección
          </h2>
          <p className="text-gray-600 mb-6">
            Ocurrió un error al cargar esta sección. Intenta de nuevo.
          </p>
          {import.meta.env.DEV && (
            <details className="text-left mb-6 p-3 bg-gray-50 rounded-lg">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                Detalles del error
              </summary>
              <pre className="mt-2 text-xs text-red-600 overflow-auto max-h-32 whitespace-pre-wrap">
                {error.message}
              </pre>
            </details>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleGoHome}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Home size={16} />
              Inicio
            </button>
            <button
              onClick={resetErrorBoundary}
              className="flex items-center gap-2 px-4 py-2 bg-[#5D1828] text-white rounded-lg hover:bg-[#7A2235] transition-colors"
            >
              <RefreshCw size={16} />
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Component-level error: Compact inline error
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-center gap-2 text-red-700">
        <AlertTriangle size={16} />
        <span className="font-medium">Error al cargar componente</span>
      </div>
      {import.meta.env.DEV && (
        <p className="mt-1 text-xs text-red-600">{error.message}</p>
      )}
      <button
        onClick={resetErrorBoundary}
        className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
      >
        Reintentar
      </button>
    </div>
  );
};
